import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '../lib/supabase'

// Database health check function
async function checkDatabaseHealth() {
  console.log('=== DATABASE HEALTH CHECK ===')

  try {
    // Test basic connection
    const { data: authData, error: authError } = await supabase.auth.getUser()
    console.log('Auth status:', authError ? 'ERROR' : 'OK', authError || authData)

    // Test profiles table
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('count')
      .limit(1)
    console.log(
      'Profiles table:',
      profilesError ? 'ERROR' : 'OK',
      profilesError || `${profilesData?.length || 0} records found`
    )

    // Removed wink table check
  } catch (err) {
    console.error('Database health check failed:', err)
  }

  console.log('=== END HEALTH CHECK ===')
}

export const useUserStore = defineStore('user', () => {
  const profile = ref(null)
  const zinInSpelStatus = ref(false)
  const zinInSpelEndTime = ref(null)
  const activeUsers = ref([])
  const likes = ref([])
  const matches = ref([])
  const error = ref(null)

  async function fetchProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select(
        `
        *,
        profile_photos(
          id,
          user_id,
          photo_url,
          is_thumbnail,
          is_primary,
          order,
          created_at
        )
      `
      )
      .eq('id', userId)
      .single()

    if (error) {
      // Handle case where profile doesn't exist yet
      if (error.code === 'PGRST116') {
        // Create a new empty profile
        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .insert({ id: userId, onboarding_complete: false })
          .select('*, profile_photos(*)')
          .single()

        if (createError) {
          error.value = createError.message
          throw createError
        }
        profile.value = { ...newProfile, profile_photos: [] } // Ensure profile_photos is an array
      } else {
        error.value = error.message
        throw error
      }
    } else {
      // Sort profile photos by order, then by created_at as fallback
      const sortedPhotos = (data.profile_photos || []).sort((a, b) => {
        // If both have order values, sort by order
        if (a.order !== null && b.order !== null) {
          return a.order - b.order
        }
        // If only one has order, prioritize it
        if (a.order !== null) return -1
        if (b.order !== null) return 1
        // If neither has order, sort by created_at
        return new Date(a.created_at) - new Date(b.created_at)
      })

      profile.value = { ...data, profile_photos: sortedPhotos }
    }
  }

  async function updateProfile(updates) {
    // Check if thumbnail_url is empty and we need to auto-populate it
    const currentThumbnailUrl = profile.value?.thumbnail_url
    if (!currentThumbnailUrl || currentThumbnailUrl.trim() === '') {
      let thumbnailUrl = null

      // Use first existing photo if available
      if (profile.value.profile_photos && profile.value.profile_photos.length > 0) {
        // Sort photos by order, then by created_at, and use the first one
        const sortedPhotos = profile.value.profile_photos.sort((a, b) => {
          if (a.order !== null && b.order !== null) {
            return a.order - b.order
          }
          if (a.order !== null) return -1
          if (b.order !== null) return 1
          return new Date(a.created_at) - new Date(b.created_at)
        })
        thumbnailUrl = sortedPhotos[0].photo_url
        console.log('Auto-populating thumbnail_url with first existing photo:', thumbnailUrl)
      }

      if (thumbnailUrl) {
        updates.thumbnail_url = thumbnailUrl
        console.log('Setting thumbnail_url in updates:', updates.thumbnail_url)
      }
    } else {
      console.log('Thumbnail URL already exists:', currentThumbnailUrl)
    }

    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', profile.value.id)
      .select(
        `
        *,
        profile_photos(
          id,
          user_id,
          photo_url,
          is_thumbnail,
          order,
          created_at
        )
      `
      ) // Re-fetch profile_photos after update
      .single()

    if (error) {
      error.value = error.message
      throw error
    }
    // Sort profile photos by order, then by created_at as fallback
    const sortedPhotos = (data.profile_photos || []).sort((a, b) => {
      // If both have order values, sort by order
      if (a.order !== null && b.order !== null) {
        return a.order - b.order
      }
      // If only one has order, prioritize it
      if (a.order !== null) return -1
      if (b.order !== null) return 1
      // If neither has order, sort by created_at
      return new Date(a.created_at) - new Date(b.created_at)
    })

    profile.value = { ...data, profile_photos: sortedPhotos }
    // After updating, check if profile is complete and update onboarding_completed status
    if (isProfileComplete.value && !profile.value.onboarding_complete) {
      await supabase
        .from('profiles')
        .update({ onboarding_complete: true })
        .eq('id', profile.value.id)
        .select()
        .single()
        .then(({ data: updatedData, error: updateError }) => {
          if (updateError) {
            console.error('Error updating onboarding_completed:', updateError)
          } else {
            profile.value = { ...updatedData, profile_photos: updatedData.profile_photos || [] }
          }
        })
    }
  }

  async function toggleZinInSpel(explorationMessage = null, durationMinutes = 90) {
    // Check if currently active and not expired
    const now = new Date()
    const isCurrentlyActive =
      profile.value?.zin_in_spel &&
      profile.value?.zin_in_spel_until &&
      new Date(profile.value.zin_in_spel_until) > now

    let updateData = {}
    let endTime = null

    if (isCurrentlyActive) {
      // If already active, replace the existing session with a new one from current time
      // This prevents accumulating hundreds of hours from repeated calls
      endTime = new Date()
      endTime.setMinutes(endTime.getMinutes() + Math.min(durationMinutes, 120))

      updateData = {
        zin_in_spel_until: endTime.toISOString(),
        last_active: new Date().toISOString()
      }
    } else {
      // If not active, start a new session
      endTime = new Date()
      endTime.setMinutes(endTime.getMinutes() + Math.min(durationMinutes, 120))

      updateData = {
        zin_in_spel: true,
        zin_in_spel_until: endTime.toISOString(),
        last_active: new Date().toISOString()
      }
    }

    // Always update exploration message - set to provided message or clear it
    if (explorationMessage && explorationMessage.trim()) {
      updateData.exploration_message = explorationMessage.trim()
    } else {
      updateData.exploration_message = null
    }

    const { data, error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', profile.value.id)
      .select()
      .single()

    if (error) {
      error.value = error.message
      throw error
    }

    profile.value = data
    zinInSpelStatus.value = true
    zinInSpelEndTime.value = endTime
  }

  async function endZinInSpel() {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        zin_in_spel: false,
        zin_in_spel_until: null,
        exploration_message: null,
        last_active: new Date().toISOString()
      })
      .eq('id', profile.value.id)
      .select()
      .single()

    if (error) {
      error.value = error.message
      throw error
      alert('Failed to end exploring. Please try again.')
    }

    profile.value = data
    zinInSpelStatus.value = false
    zinInSpelEndTime.value = null
  }

  async function fetchActiveUsers() {
    const now = new Date().toISOString()
    const { data: users, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('zin_in_spel', true)
      .gt('zin_in_spel_until', now)
      .order('last_active', { ascending: false })

    if (error) {
      error.value = error.message
      throw error
    }

    // Filter out anonymous users at the data retrieval level
    const validUsers = users.filter(user => !isAnonymousUser(user))

    // Extract user IDs for batch photo fetching
    const userIds = validUsers.map(user => user.id)

    if (userIds.length === 0) {
      // No valid active users
      activeUsers.value = []
      return
    }

    // Batch fetch all photos for active users
    const { data: allPhotos, error: photosError } = await supabase
      .from('profile_photos')
      .select('*')
      .in('user_id', userIds)
      .order('created_at', { ascending: true })

    if (photosError) {
      error.value = photosError.message
      console.error('Error fetching profile photos:', photosError)
      // Fallback to empty photos if batch fetch fails
      activeUsers.value = validUsers.map(user => ({ ...user, profile_photos: [] }))
      return
    }

    // Group photos by user ID
    const photosByUserId = {}
    allPhotos.forEach(photo => {
      if (!photosByUserId[photo.user_id]) {
        photosByUserId[photo.user_id] = []
      }
      photosByUserId[photo.user_id].push(photo)
    })

    // Merge photos into user objects
    const usersWithPhotos = validUsers.map(user => ({
      ...user,
      profile_photos: photosByUserId[user.id] || []
    }))

    // Final safety filter to ensure no anonymous users slip through
    activeUsers.value = usersWithPhotos.filter(user => !isAnonymousUser(user))
  }

  // Helper function to check if a user is anonymous
  function isAnonymousUser(user) {
    if (!user || typeof user !== 'object') return true

    const name = user.name

    // Check if name is null, undefined, empty string, or contains only whitespace
    return name === null || name === undefined || (typeof name === 'string' && name.trim() === '')
  }

  async function fetchAllUsers() {
    const { data: users, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      error.value = error.message
      throw error
    }

    // Filter out anonymous users at the data retrieval level
    const validUsers = users.filter(user => !isAnonymousUser(user))

    // Batch fetch photos for valid users only
    const userIds = validUsers.map(user => user.id)

    if (userIds.length === 0) {
      // Return empty array if no valid users
      return []
    }

    const { data: allPhotos, error: photosError } = await supabase
      .from('profile_photos')
      .select('*')
      .in('user_id', userIds)
      .order('created_at', { ascending: true })

    if (photosError) {
      error.value = photosError.message
      console.error('Error fetching profile photos:', photosError)
      // Return users without photos if photo fetch fails
      return validUsers.map(user => ({ ...user, profile_photos: [] }))
    }

    // Group photos by user ID
    const photosByUserId = {}
    allPhotos.forEach(photo => {
      if (!photosByUserId[photo.user_id]) {
        photosByUserId[photo.user_id] = []
      }
      photosByUserId[photo.user_id].push(photo)
    })

    // Merge photos into user objects and apply final filter as safety measure
    const usersWithPhotos = validUsers.map(user => ({
      ...user,
      profile_photos: photosByUserId[user.id] || []
    }))

    // Final safety filter to ensure no anonymous users slip through
    return usersWithPhotos.filter(user => !isAnonymousUser(user))
  }

  async function toggleLike(targetUserId) {
    const isLiked = likes.value.includes(targetUserId)

    if (isLiked) {
      // Unlike
      const { error } = await supabase
        .from('likes')
        .delete()
        .eq('user_id', profile.value.id)
        .eq('target_user_id', targetUserId)

      if (error) {
        error.value = error.message
        throw error
      }
      likes.value = likes.value.filter(id => id !== targetUserId)
    } else {
      // Like
      const { error } = await supabase.from('likes').insert({
        user_id: profile.value.id,
        target_user_id: targetUserId
      })

      if (error) {
        error.value = error.message
        // If it's a duplicate key error, sync state and continue
        if (error.code === '23505') {
          await fetchLikes()
          return
        }
        throw error
      }
      likes.value.push(targetUserId)
    }

    // Refresh likes to ensure consistency
    await fetchLikes()
  }

  async function fetchLikes() {
    if (!profile.value || !profile.value.id) {
      return
    }

    const { data, error } = await supabase
      .from('likes')
      .select('target_user_id')
      .eq('user_id', profile.value.id)

    if (error) {
      error.value = error.message
      throw error
    }
    likes.value = data.map(like => like.target_user_id)
  }

  async function fetchMatches() {
    const { data, error } = await supabase
      .from('matches')
      .select('*')
      .or(`user1_id.eq.${profile.value.id},user2_id.eq.${profile.value.id}`)

    if (error) {
      error.value = error.message
      throw error
    }
    matches.value = data
  }

  const requiredFields = ['name']

  const isProfileComplete = computed(() => {
    if (!profile.value) return false

    const allRequiredFieldsFilled = requiredFields.every(field => {
      const value = profile.value[field]
      return value !== null && value !== undefined && String(value).trim() !== ''
    })

    const hasProfilePhoto = profile.value.profile_photos && profile.value.profile_photos.length > 0

    return allRequiredFieldsFilled && hasProfilePhoto
  })

  // Admin functionality
  const isAdmin = computed(() => profile.value?.is_admin === true)

  function requireAdmin() {
    if (!isAdmin.value) {
      throw new Error('Admin access required')
    }
  }

  async function fetchAllUsersForAdmin() {
    requireAdmin()

    // For admin functionality, we'll use the existing fetchAllUsers function
    // which already has proper filtering and works with current RLS policies
    try {
      const allUsers = await fetchAllUsers()

      // Add the current user's admin status and other admin-relevant info
      // by fetching additional admin-specific data if needed
      const usersWithAdminInfo = allUsers.map(user => ({
        ...user
        // Note: is_admin field should be included in the basic profile data
      }))

      return usersWithAdminInfo
    } catch (error) {
      throw new Error('Failed to fetch users for admin panel: ' + error.message)
    }
  }

  async function promoteToAdmin(userId) {
    requireAdmin()

    const { data, error } = await supabase.rpc('promote_user_to_admin', {
      target_user_id: userId
    })

    if (error) throw error
    if (!data) throw new Error('Failed to promote user to admin')
  }

  async function demoteAdmin(userId) {
    requireAdmin()

    const { data, error } = await supabase.rpc('demote_user_from_admin', {
      target_user_id: userId
    })

    if (error) throw error
    if (!data) throw new Error('Failed to demote admin user')
  }

  return {
    profile,
    zinInSpelStatus,
    zinInSpelEndTime,
    activeUsers,
    likes,
    matches,
    fetchProfile,
    updateProfile,
    toggleZinInSpel,
    endZinInSpel,
    fetchActiveUsers,
    toggleLike,
    fetchLikes,
    fetchMatches,
    checkDatabaseHealth,
    fetchAllUsers,
    isProfileComplete, // Expose the computed property
    requiredFields, // Expose required fields for UI
    isAnonymousUser, // Expose the anonymous user check function
    // Admin functionality
    isAdmin,
    fetchAllUsersForAdmin,
    promoteToAdmin,
    demoteAdmin
  }
})
